/**
 * Verify email — ELF-CIRCLE.
 *
 * Four states: verifying, verified, failed, and reached without a token. The
 * failed and no-token states both offer a resend, so that form is written once
 * and shared. Presentation only — `verifyEmail` and `resendVerification` and
 * their result handling are the template's. See ElfAuthShell for the chrome.
 */

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, AlertTriangle, Check, RefreshCw, ArrowRight } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuthContext } from '../context/AuthContext';
import { ElfAuthShell } from '../components';
import { route } from '../../../shared/utils';

type VerificationState = 'loading' | 'success' | 'error' | 'no-token';

/**
 * The auth client passes the transport error straight through, so a stale
 * token surfaces as "HTTP 400: Bad Request". Nothing a person can act on —
 * swap those for the sentence that actually describes what happened.
 */
function humanise(message: string | null): string {
  if (!message || /^HTTP \d{3}/i.test(message) || /^(bad request|forbidden|not found)$/i.test(message)) {
    return 'This verification link has expired or has already been used.';
  }
  return message;
}

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [verificationState, setVerificationState] = useState<VerificationState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  const { verifyEmail, resendVerification } = useAuthContext();

  useEffect(() => {
    if (!token) {
      setVerificationState('no-token');
      return;
    }

    const handleVerifyEmail = async () => {
      try {
        const result = await verifyEmail({ token });
        if (result.success) {
          setVerificationState('success');
        } else {
          setVerificationState('error');
          setErrorMessage(result.error || 'Verification failed');
        }
      } catch (error) {
        setVerificationState('error');
        setErrorMessage(error instanceof Error ? error.message : 'Verification failed');
      }
    };

    handleVerifyEmail();
  }, [token, verifyEmail]);

  const handleResendVerification = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!resendEmail) {
      setResendMessage('Please enter your email address');
      return;
    }

    setResendLoading(true);
    setResendMessage(null);

    try {
      const result = await resendVerification(resendEmail);
      if (result.success) {
        setResendMessage('Verification email sent! Please check your inbox.');
      } else {
        setResendMessage(result.error || 'Failed to send verification email');
      }
    } catch (error) {
      setResendMessage(
        error instanceof Error ? error.message : 'Failed to send verification email',
      );
    } finally {
      setResendLoading(false);
    }
  };

  // Shared by the failed and no-token states — the only difference is the copy
  // above it, which each state supplies.
  const resendForm = (
    <form onSubmit={handleResendVerification} className="elf-auth__form" noValidate>
      <label className="elf-auth__field">
        <span>Email</span>
        <input
          name="resend-email"
          type="email"
          autoComplete="email"
          required
          value={resendEmail}
          onChange={(e) => setResendEmail(e.target.value)}
          placeholder="you@company.com"
        />
      </label>

      <button type="submit" className="pillcta elf-auth__submit" disabled={resendLoading}>
        {resendLoading ? 'Sending…' : 'Send a new link'}
        <span aria-hidden="true">
          {resendLoading ? <Loader2 className="elf-auth__spin" /> : <RefreshCw />}
        </span>
      </button>

      {resendMessage && (
        <p className="elf-auth__note" role="status">
          {resendMessage}
        </p>
      )}

      <p className="elf-auth__alt">
        Already verified?{' '}
        <Link className="elf-auth__link" to={route('/auth/login')}>
          Sign in
        </Link>
      </p>
    </form>
  );

  const copy: Record<VerificationState, { kicker: string; title: string; lede?: string }> = {
    loading: { kicker: 'Verifying', title: 'One moment', lede: 'Checking your verification link.' },
    success: { kicker: 'Verified', title: "You're in" },
    error: {
      kicker: 'Verification',
      title: "That link didn't work",
      lede: 'Verification links expire. Send yourself a fresh one.',
    },
    'no-token': {
      kicker: 'Verification',
      title: "This link isn't valid",
      lede: 'Send yourself a fresh one and open it straight from the email.',
    },
  };

  const { kicker, title, lede } = copy[verificationState];

  return (
    <>
      <SEO
        title="Verify email"
        description="Verify your email address to finish setting up your ELF-CIRCLE account."
        keywords="email verification, verify account"
      />

      <ElfAuthShell
        kicker={kicker}
        title={title}
        lede={lede}
        artHeadline="One step from the room"
        artLede="Verifying your address keeps the circle to the people actually in it."
      >
        {verificationState === 'loading' && (
          <div className="elf-auth__checking">
            <Loader2 className="elf-auth__spin" aria-hidden="true" />
            <span>Verifying your email…</span>
          </div>
        )}

        {verificationState === 'success' && (
          <div className="elf-auth__done">
            <span className="elf-auth__tick" aria-hidden="true">
              <Check />
            </span>
            <p className="elf-auth__note">
              Your email is verified and the account is active. Sign in and you&rsquo;re away.
            </p>
            <Link className="pillcta elf-auth__submit" to={route('/auth/login')}>
              Continue to sign in
              <span aria-hidden="true">
                <ArrowRight />
              </span>
            </Link>
          </div>
        )}

        {verificationState === 'error' && (
          <div className="elf-auth__done">
            <span className="elf-auth__tick elf-auth__tick--warn" aria-hidden="true">
              <AlertTriangle />
            </span>
            <p className="elf-auth__note">{humanise(errorMessage)}</p>
            {resendForm}
          </div>
        )}

        {verificationState === 'no-token' && (
          <div className="elf-auth__done">
            <span className="elf-auth__tick elf-auth__tick--warn" aria-hidden="true">
              <AlertTriangle />
            </span>
            <p className="elf-auth__note">
              This verification link is missing its token, which usually means the email client cut
              the URL short.
            </p>
            {resendForm}
          </div>
        )}
      </ElfAuthShell>
    </>
  );
};

export default VerifyEmailPage;
